import { FC, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { DropdownMenu } from 'radix-ui';
import {
  User as UserIcon,
  MessageCircle,
  Library,
  HelpCircle,
  LogOut,
  FileText,
  FileBadge,
  GraduationCap,
  LucideIcon,
  ScrollText,
} from 'lucide-react';
import { cn } from '@new/lib';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { User, Role, useDownloadEnrollmentVerificationLetterMutation } from '@teachur/http-client';
import { ContactModal } from '@screens/ContactModal';

const ICON_SIZE = 18;
const ICON_STROKE_WIDTH = 1.5;

const DROPDOWN_ITEM_BASE = cn(
  'flex items-center gap-3 px-4 py-2',
  'text-(--neutral-black-NB600) hover:bg-(--neutral-black-NB100) hover:text-(--primary-O1000)',
  'cursor-pointer outline-none transition-all duration-200',
  'data-[highlighted]:bg-(--neutral-black-NB100) data-[highlighted]:text-(--primary-O1000)'
);

const LABEL_CLASS = 'text-sm px-4';

const SEPARATOR_CLASS = 'h-px bg-(--neutral-black-NB100) my-1';

const DROPDOWN_CONTENT_CLASS = cn(
  'min-w-[240px] bg-(--neutral-white-NW1000) rounded-md shadow-lg',
  'border border-(--neutral-black-NB100)',
  'py-2 z-50',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95'
);

type MenuItem =
  | {
      type: 'item';
      label: string;
      icon?: LucideIcon;
      path?: string;
      href?: string;
      action?: 'logout' | 'contact' | 'downloadEnrollmentLetter';
      studentOnly?: boolean;
    }
  | {
      type: 'separator';
    };

const MENU_ITEMS: MenuItem[] = [
  {
    type: 'item',
    label: 'Profile',
    icon: UserIcon,
    path: '/edit-profile',
  },
  {
    type: 'item',
    label: 'Explore Degrees',
    icon: GraduationCap,
    path: '/degrees',
  },
  {
    type: 'item',
    studentOnly: true,
    label: 'Transcripts',
    icon: ScrollText,
    path: '/transcripts',
  },
  {
    type: 'item',
    label: 'Download Proof of Enrollment',
    icon: FileBadge,
    action: 'downloadEnrollmentLetter',
    studentOnly: true,
  },
  {
    type: 'item',
    label: 'Newlane Tutorials',
    icon: FileText,
    path: '/newlane-tutorials',
  },
  {
    type: 'item',
    label: 'Chat',
    icon: MessageCircle,
    path: '/chat',
  },
  {
    type: 'item',
    label: 'Library',
    icon: Library,
    path: '/library',
  },
  {
    type: 'item',
    label: 'Contact us',
    icon: HelpCircle,
    action: 'contact',
  },
  { type: 'separator' },
  {
    type: 'item',
    label: 'Terms of Use',
    path: '/agreements/termsOfUse',
  },
  {
    type: 'item',
    label: 'User Agreement',
    path: '/agreements/userAgreement',
  },
  { type: 'separator' },
  {
    type: 'item',
    label: 'Sign out',
    icon: LogOut,
    action: 'logout',
  },
];

const DEFAULT_LETTER_ERROR = 'We couldn’t generate your enrollment letter. Please try again later.';

async function resolveDownloadErrorMessage(error: unknown): Promise<string> {
  const data = (error as { response?: { data?: unknown } })?.response?.data;

  const asBlob = data as { text?: () => Promise<string> } | undefined;
  if (asBlob && typeof asBlob.text === 'function') {
    try {
      const parsed = JSON.parse(await asBlob.text());
      if (typeof parsed?.message === 'string') {
        return parsed.message;
      }
    } catch {
      // ignore
    }
  } else if (typeof (data as { message?: unknown })?.message === 'string') {
    return (data as { message: string }).message;
  }

  return DEFAULT_LETTER_ERROR;
}

interface ProfileDropdownProps {
  user: User;
}

export const ProfileDropdown: FC<ProfileDropdownProps> = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const { mutate: downloadEnrollmentLetter, isPending: isDownloadingLetter } =
    useDownloadEnrollmentVerificationLetterMutation({
      onError: async (error) => {
        toast.error(await resolveDownloadErrorMessage(error));
      },
    });

  const avatarUrl = user.avatar
    ? user.avatar.url
    : `https://eu.ui-avatars.com/api/?name=${user.firstName}+${user.lastName}`;

  const isStudent = user.role === Role.Student;
  const menuItems = MENU_ITEMS.filter(
    (item) => item.type === 'separator' || !item.studentOnly || isStudent
  );

  const onSelect = (item: MenuItem) => {
    if (item.type === 'separator') return;

    if (item.action === 'logout') {
      localStorage.removeItem('accessToken');
      queryClient.clear();
      navigate({ to: '/login' });
    } else if (item.action === 'contact') {
      setIsContactModalOpen(true);
    } else if (item.action === 'downloadEnrollmentLetter') {
      if (isDownloadingLetter) return;
      downloadEnrollmentLetter();
    } else if (item.path) {
      navigate({ to: item.path });
    } else if (item.href) {
      window.open(item.href, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="flex items-center gap-2 cursor-pointer outline-none border-none rounded-2xl! hover:bg-gray-100 transition-colors duration-200">
            <img
              src={avatarUrl}
              alt={`${user.firstName} ${user.lastName}`}
              className="rounded-full w-11 h-11 object-cover shrink-0"
            />
            <span
              className="hidden sm:inline whitespace-nowrap text-[14px] font-medium leading-[125%]"
              style={{ color: 'var(--Neutral-White-NW600, #8B9FA7)', fontFamily: 'Montserrat' }}
            >
              {user.firstName}
            </span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content className={DROPDOWN_CONTENT_CLASS} sideOffset={5} align="end">
            <DropdownMenu.Label className={LABEL_CLASS}>
              <span className="font-semibold text-(--neutral-black-NB600)">
                {user.firstName} {user.lastName}
              </span>
            </DropdownMenu.Label>

            <DropdownMenu.Separator className={SEPARATOR_CLASS} />

            {menuItems.map((item, index) => {
              if (item.type === 'separator') {
                return (
                  <DropdownMenu.Separator key={`separator-${index}`} className={SEPARATOR_CLASS} />
                );
              }

              if (item.label === 'Transcripts' && user.role !== 'student') {
                return null;
              }

              const Icon = item.icon;

              return (
                <DropdownMenu.Item
                  key={item.label}
                  onSelect={() => onSelect(item)}
                  className={DROPDOWN_ITEM_BASE}
                >
                  {Icon && <Icon size={ICON_SIZE} strokeWidth={ICON_STROKE_WIDTH} />}
                  <span>{item.label}</span>
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </>
  );
};
